/* eslint-disable indent */
import { usePostHog } from 'posthog-js/react';
import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { EventEmitter } from '~utils/event_emitter';

const POSTHOG_PROJECT_API_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;

interface iPosthogEvent {
  event: string;
  properties?: object;
}
export const posthogEvent = new EventEmitter<iPosthogEvent>();

function useProductAnalytics() {
  let initialised: boolean = false;
  const enabled = POSTHOG_PROJECT_API_KEY != null;
  const posthog = usePostHog();
  const location = useLocation();

  const postHogOptions = {
    api_host: 'https://d3gcparwh40s87.cloudfront.net',
    ui_host: 'https://eu.posthog.com',
  };

  const initialise = async (userId: string, customer: string) => {
    posthog.register({
      // Set super properties
      'intelliprove.AppName': 'health_dashboard',
      'intelliprove.IsDevKey': customer.endsWith('-DEV') || customer.endsWith('-TESTING') || customer.includes('INTELLIPROVE'),
    });
    const phUserId = userId || posthog.get_distinct_id();

    posthog.identify(phUserId);
    posthog.people.set({
      $userId: phUserId,
      'intelliprove.UserId': userId,
    });
    posthog.group('customer', customer, {
      Name: customer,
      Type: customer.endsWith('-DEV') ? 'Development' : 'Production',
    });
    posthog.setGroupPropertiesForFlags({ customer: { Name: customer } });
    initialised = true;
  };

  const getUserReferenceId = () => {
    if (!initialised) throw new Error('Product analytics service is not yet initialised.');
    return posthog.get_distinct_id();
  };

  useEffect(() => {
    const unsub = posthogEvent.subscribe((evt: iPosthogEvent) => {
      const props = {
        pathname: location.pathname,
        pathkey: location.key,
        ...evt.properties,
      };
      posthog.capture(evt.event, props);
    });

    return () => unsub();
  }, []);

  return {
    analyticsInitialised: initialised,
    analyticsEnabled: enabled,
    initialiseAnalytics: initialise,
    getUserReferenceId,
    postHogApiKey: POSTHOG_PROJECT_API_KEY,
    postHogOptions,
  };
}

export default useProductAnalytics;
